import Box, { BoxProps } from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import * as React from 'react';

import {
  AgreementDateOperators,
  FieldName,
  FileNoOperators,
  SearchField
} from '../models/SearchField';
import newSearchField from '../services/newSearchField';
import DateSearchField from './DateSearchField';
import DropdownSearchField from './DropdownSearchField';
import SearchFieldNew from './SearchFieldNew';
import TextSearchField from './TextSearchField';

export interface Props {
  onChangeField: (searchField: Partial<SearchField>) => void;
  onClearFields: () => void;
  onRemoveField: (id: string) => void;
  onReplaceField: (newSearchField: SearchField) => void;
  onNewField: (newField: SearchField) => void;
  companyOptions: string[];
  searchFields: any[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    field: {
      marginBottom: theme.spacing(1)
    }
  })
);

const Search: React.FC<Props & BoxProps> = ({
  onChangeField,
  onClearFields,
  onRemoveField,
  onReplaceField,
  onNewField,
  searchFields,
  companyOptions,
  ...boxProps
}) => {
  const classes = useStyles();

  const [agreementStatus, setAgreementStatus] = React.useState<
    'Active' | 'Proposed' | 'Cancelled' | null
  >(null);

  const mapCurrentSearchFilters = () => {
    return searchFields.reduce((acc, field) => {
      if (!acc[field.fieldName]) {
        acc[field.fieldName] = field.operator ? [field.operator] : [];
        return acc;
      }
      field.operator && acc[field.fieldName].push(field.operator);
      return acc;
    }, {});
  };

  const currentSearchFiltersMap = mapCurrentSearchFilters();

  const availableNewFields = (): FieldName[] => {
    const fieldNames: FieldName[] = [
      'Agreement Date',
      'File No',
      'Company Name',
      'Notes',
      'Initial Agreement'
    ];

    return fieldNames.filter(option => {
      if (!currentSearchFiltersMap[option]) {
        return option;
      }
      if (
        option === 'File No' &&
        currentSearchFiltersMap[option][0] !== '≥' &&
        currentSearchFiltersMap[option][0] !== '≤'
      ) {
        return option;
      }
      if (
        currentSearchFiltersMap[option].length === 1 &&
        currentSearchFiltersMap[option][0] !== '=' &&
        currentSearchFiltersMap[option][0] !== '≈'
      ) {
        return option;
      }
      return null;
    });
  };

  const newSearchFieldOperator = (selectedOption: FieldName) => {
    if (!currentSearchFiltersMap[selectedOption]) {
      return null;
    }
    if (currentSearchFiltersMap[selectedOption][0] === '≥') {
      return '≤';
    }
    if (currentSearchFiltersMap[selectedOption][0] === '≤') {
      return '≥';
    }
    return null;
  };

  const onSelectNewField = (selectedOption: FieldName) => {
    onNewField(newSearchField(selectedOption, newSearchFieldOperator(selectedOption)));
  };

  const onSelectReplaceField = (field: Pick<SearchField, 'fieldName' | 'id'>) => {
    onReplaceField(
      newSearchField(field.fieldName, newSearchFieldOperator(field.fieldName), field.id)
    );
  };

  const availableNewOperators = (
    fieldName: Extract<FieldName, 'Agreement Date' | 'File No'>,
    operator: FileNoOperators | AgreementDateOperators
  ) => {
    if (
      currentSearchFiltersMap[fieldName] === ['='] ||
      currentSearchFiltersMap[fieldName] === ['≈']
    ) {
      return ['≥', '≤'];
    }
    if (currentSearchFiltersMap[fieldName] === ['≥']) {
      return ['≤'];
    }
    if (currentSearchFiltersMap[fieldName] === ['≤']) {
      return ['≥'];
    }
    if (
      currentSearchFiltersMap[fieldName].includes('≤') &&
      currentSearchFiltersMap[fieldName].includes('≥')
    ) {
      return [];
    }
    if (fieldName === 'Agreement Date') {
      const agreementDateOperators: AgreementDateOperators[] = ['=', '≥', '≤'];
      return agreementDateOperators.filter(newOperator => newOperator !== operator);
    }
    if (fieldName === 'File No') {
      const fileNoOperators: FileNoOperators[] = ['=', '≈', '≥', '≤'];

      if (currentSearchFiltersMap[fieldName].length > 1) {
        return ['=', '≈'];
      }

      return fileNoOperators.filter(newOperator => newOperator !== operator);
    }
    return [];
  };

  const checkForDisabledOperator = (fieldName: FieldName) => {
    if (
      currentSearchFiltersMap[fieldName].includes('≤') &&
      currentSearchFiltersMap[fieldName].includes('≥')
    ) {
      return true;
    }
    return false;
  };

  const setAgreementFilter = (status: 'Active' | 'Proposed' | 'Cancelled') => {
    if (agreementStatus === null) {
      setAgreementStatus(status);
      onNewField({ fieldName: 'Status', value: [status], id: 'STATUS' });
      return;
    }
    if (agreementStatus === status) {
      setAgreementStatus(null);
      onRemoveField('STATUS');
      return;
    }
    setAgreementStatus(status);
    onChangeField({ fieldName: 'Status', value: [status], id: 'STATUS' });
  };

  return (
    <Box display="flex" flexDirection="column" overflow="hidden" {...boxProps}>
      <Box overflow="hidden auto" p={2} display="flex" style={{ gap: 5 }}>
        <Chip
          label="Active"
          variant={agreementStatus === 'Active' ? undefined : 'outlined'}
          onClick={() => setAgreementFilter('Active')}
        />
        <Chip
          label="Proposed"
          variant={agreementStatus === 'Proposed' ? undefined : 'outlined'}
          onClick={() => setAgreementFilter('Proposed')}
        />
        <Chip
          label="Cancelled"
          variant={agreementStatus === 'Cancelled' ? undefined : 'outlined'}
          onClick={() => setAgreementFilter('Cancelled')}
        />
      </Box>
      <Box component="article" flex="1 1 0%" overflow="hidden auto" p={2}>
        {searchFields &&
          searchFields.map((searchField: SearchField) => {
            switch (searchField.fieldName) {
              case 'Agreement Date':
                return (
                  <Box key={searchField.id} className={classes.field}>
                    <DateSearchField
                      onChangeField={onChangeField}
                      onRemoveField={onRemoveField}
                      onSelectReplaceField={onSelectReplaceField}
                      availableNewOperators={availableNewOperators(
                        searchField.fieldName,
                        searchField.operator
                      )}
                      isOperatorDisabled={checkForDisabledOperator(searchField.fieldName)}
                      fieldOptions={availableNewFields().filter(
                        field => field !== searchField.fieldName
                      )}
                      searchField={searchField}
                    />
                  </Box>
                );
              case 'Company Name':
                return (
                  <Box key={searchField.id} className={classes.field}>
                    <DropdownSearchField
                      onChangeField={onChangeField}
                      onRemoveField={onRemoveField}
                      onSelectReplaceField={onSelectReplaceField}
                      options={companyOptions}
                      fieldOptions={availableNewFields()}
                      searchField={searchField}
                    />
                  </Box>
                );
              case 'File No':
                return (
                  <Box key={searchField.id} className={classes.field}>
                    <TextSearchField
                      onChangeField={onChangeField}
                      onRemoveField={onRemoveField}
                      onSelectReplaceField={onSelectReplaceField}
                      availableNewOperators={availableNewOperators(
                        searchField.fieldName,
                        searchField.operator
                      )}
                      isOperatorDisabled={checkForDisabledOperator('File No')}
                      searchField={searchField}
                      fieldOptions={availableNewFields().filter(
                        field => field !== searchField.fieldName
                      )}
                    />
                  </Box>
                );
              case 'Notes':
              case 'Initial Agreement':
                return (
                  <Box key={searchField.id} className={classes.field}>
                    <TextSearchField
                      onChangeField={onChangeField}
                      onRemoveField={onRemoveField}
                      onSelectReplaceField={onSelectReplaceField}
                      availableNewOperators={[]}
                      isOperatorDisabled={true}
                      searchField={searchField}
                      fieldOptions={availableNewFields()}
                    />
                  </Box>
                );
              default:
                return null;
            }
          })}
        {availableNewFields().length > 0 && (
          <SearchFieldNew onSelect={onSelectNewField} availableOptions={availableNewFields()} />
        )}
      </Box>
      <Divider variant="fullWidth" />
      <Box flex="0 0 auto" display="flex" flexDirection="row" py={1} px={1.5}>
        <Button
          onClick={() => {
            onClearFields();
            setAgreementStatus(null);
          }}
          disabled={searchFields.length === 0}
        >
          Clear
        </Button>
      </Box>
    </Box>
  );
};

export default Search;
